import { useResponse } from 'contexts/response';
import { useCallback } from 'react';
import { fetchInvoicePdfsZip } from 'services';

import { Invoice, InvoiceInterface, InvoiceSummary, InvoiceSummaryInterface } from 'types';

const BASE_URL = new URL('/api/invoices', window.location.origin);

export const useInvoiceApi = () => {
  const { handleError, setSuccess } = useResponse();

  const generateInvoices = useCallback(
    async (params: { customerIds?: number[]; from: Date; to: Date; locationId?: number, splitLoaded?: boolean }): Promise<InvoiceSummary[] | null> => {
      const GET_INIT: RequestInit = {
        method: 'GET',
        headers: new Headers({
          Authorization: `Bearer ${localStorage.getItem('token')}`
        })
      };

      const url = new URL(`${BASE_URL}/generate`);
      url.searchParams.append('fromDate', params.from.toISOString());
      url.searchParams.append('toDate', params.to.toISOString());
      // No ids means every customer. The backend must understand customerIds: an older one ignores
      // it and would generate for everyone, which is why the REST change deploys first.
      params.customerIds?.forEach(id => url.searchParams.append('customerIds', `${id}`));
      if (params.locationId !== undefined) {
        url.searchParams.append('locationId', `${params.locationId}`);
      }
      if (params.splitLoaded !== undefined) {
        url.searchParams.append('splitLoaded', `${params.splitLoaded}`);
      }

      try {
        const response = await fetch(url, GET_INIT);

        if (response.ok) {
          const json = await response.json();
          setSuccess(`Invoices generated successfully!`);
          // /generate returns the summary projection, not the full Invoice graph (it used to send
          // ~4.2MB of items/billable events per location-month that this caller discarded).
          // InvoiceSummary falls back to the legacy entity math when `items` is present, so this
          // keeps working against an older backend during a rolling deploy.
          return json?.map((invoice: InvoiceSummaryInterface) => new InvoiceSummary(invoice)) ?? null;
        }

        switch (response.status) {
          case 504:
            setSuccess('Invoices are being processed. Please refresh the page later to view the invoices.');
            return null;
          case 500:
            handleError('INTERNAL SERVER ERROR');
            return null;
          case 403:
            handleError('FORBIDDEN');
            return null;
          case 401:
            handleError('UNAUTHORIZED');
            return null;
          default:
            const json = await response.json();
            handleError(json.message?.match(/"[^"]+"/)?.[0] ?? response.statusText);
            return null;
        }
      } catch (e) {
        handleError(e);
        return null;
      }
    },
    [handleError, setSuccess]
  );

  // What generation would charge each customer for the period, as a CSV, without creating
  // invoices. Saved under the server's filename; resolves to whether the download happened.
  const downloadChargesPreview = useCallback(
    async (params: { locationId: number; from: Date; to: Date; customerIds?: number[] }): Promise<boolean> => {
      const url = new URL(`${BASE_URL}/preview/csv`);
      url.searchParams.append('locationId', `${params.locationId}`);
      url.searchParams.append('fromDate', params.from.toISOString());
      url.searchParams.append('toDate', params.to.toISOString());
      params.customerIds?.forEach(id => url.searchParams.append('customerIds', `${id}`));

      try {
        // No Accept: text/csv here: errors come back as JSON, and their message is what we show.
        const response = await fetch(url, {
          method: 'GET',
          headers: new Headers({ Authorization: `Bearer ${localStorage.getItem('token')}` })
        });

        if (response.ok) {
          const blob = await response.blob();
          const filename =
            response.headers.get('Content-Disposition')?.match(/filename="([^"]+)"/)?.[1] ?? 'charges-preview.csv';
          const fileUrl = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = fileUrl;
          link.download = filename;
          link.click();
          window.URL.revokeObjectURL(fileUrl);
          return true;
        }

        switch (response.status) {
          case 504:
            handleError('The preview took too long. Try a shorter period or fewer customers.');
            return false;
          case 500:
            handleError('INTERNAL SERVER ERROR');
            return false;
          case 403:
            handleError('FORBIDDEN');
            return false;
          case 401:
            handleError('UNAUTHORIZED');
            return false;
          default:
            const json = await response.json();
            handleError(json.message?.match(/"[^"]+"/)?.[0] ?? response.statusText);
            return false;
        }
      } catch (e) {
        handleError(e);
        return false;
      }
    },
    [handleError]
  );

  const deleteInvoice = useCallback(
    async (invoiceId: number): Promise<null> => {
      const DELETE_INIT: RequestInit = {
        method: 'DELETE',
        headers: new Headers({ Authorization: `Bearer ${localStorage.getItem('token')}` })
      };
      const response = await fetch(`${BASE_URL}/${invoiceId}`, DELETE_INIT);
      if (response.ok) {
        setSuccess(`Invoice ${invoiceId} deleted.`);
        return null;
      }
      switch (response.status) {
        case 500:
          handleError('INTERNAL SERVER ERROR');
          return null;
        case 403:
          handleError('FORBIDDEN');
          return null;
        case 401:
          handleError('UNAUTHORIZED');
          return null;
        default:
          const json = await response.json();
          handleError(json.message?.match(/"[^"]+"/)?.[0] ?? response.statusText);
          return null;
      }
    },
    [handleError, setSuccess]
  );

  const bulkDeleteInvoices = useCallback(
    async (ids: number[]): Promise<null> => {
      const DELETE_INIT: RequestInit = {
        method: 'DELETE',
        headers: new Headers({ Authorization: `Bearer ${localStorage.getItem('token')}` })
      };
      const url = new URL(`${BASE_URL}/bulkDelete`);
      ids.forEach(id => url.searchParams.append('ids', `${id}`));
      const response = await fetch(url, DELETE_INIT);
      if (response.ok) {
        setSuccess(`Invoices deleted.`);
        return null;
      }
      switch (response.status) {
        case 500:
          handleError('INTERNAL SERVER ERROR');
          return null;
        case 403:
          handleError('FORBIDDEN');
          return null;
        case 401:
          handleError('UNAUTHORIZED');
          return null;
        default:
          const json = await response.json();
          handleError(json.message?.match(/"[^"]+"/)?.[0] ?? response.statusText);
          return null;
      }
    },
    [handleError, setSuccess]
  );

  const finalizeInvoicesForLocation = useCallback(
    async (locationId: number): Promise<Invoice[] | null> => {
      const PUT_INIT: RequestInit = {
        method: 'PUT',
        headers: new Headers({ Authorization: `Bearer ${localStorage.getItem('token')}` })
      };

      const url = new URL(`${BASE_URL}/finalize/${locationId}`);

      try {
        const response = await fetch(url, PUT_INIT);
        const json = await response.json();

        await Promise.allSettled([response, json]);

        if (response.ok) {
          setSuccess(`Invoices finalized successfully!`);
          const temp = json?.map((invoice: InvoiceInterface) => new Invoice(invoice)) ?? null;
          return temp;
        }

        switch (response.status) {
          case 500:
            handleError('INTERNAL SERVER ERROR');
            return null;
          case 403:
            handleError('FORBIDDEN');
            return null;
          case 401:
            handleError('UNAUTHORIZED');
            return null;
          default:
            handleError(json.message?.match(/"[^"]+"/)?.[0] ?? response.statusText);
            return null;
        }
      } catch (e) {
        handleError(e);
        return null;
      }
    },
    [handleError, setSuccess]
  );

  // Downloads every selected invoice's PDFs as one server-built ZIP. The browser buffers the
  // archive before saving, so the caller's loader stays visible until the last byte arrives.
  const bulkDownloadPdfs = useCallback(
    async (ids: number[]): Promise<boolean> => {
      try {
        const { blob, filename } = await fetchInvoicePdfsZip(ids);
        const fileUrl = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = fileUrl;
        link.download = filename;
        link.click();
        link.remove();
        // Give the browser a tick to start the download before releasing the object URL.
        window.setTimeout(() => window.URL.revokeObjectURL(fileUrl), 0);

        setSuccess(`${ids.length} invoice PDF${ids.length === 1 ? '' : 's'} downloaded.`);
        return true;
      } catch (e) {
        if (e instanceof Response) {
          // Never let a consumed/empty error body turn into a second unhandled read.
          const json = await e.json().catch(() => ({}));
          handleError(e, json);
        } else {
          handleError(e);
        }
        return false;
      }
    },
    [handleError, setSuccess]
  );

  return {
    generateInvoices,
    downloadChargesPreview,
    deleteInvoice,
    bulkDeleteInvoices,
    finalizeInvoicesForLocation,
    bulkDownloadPdfs
  };
};
